import { useContext, useMemo, useState } from 'react'
import { Lock } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import Loader from '../Loader/Loader'
import FeatureList from './FeatureList'
import ConfirmPlanChangeDialog from './ConfirmPlanChangeDialog'
import EnableSeatDialog from './EnableSeatDialog'
import { Product } from '../../models/Product'
import useProducts from '../../hooks/queries/useProducts'
import useSubscribe from '../../hooks/useSubscribe'
import AuthContext from '../../context/AuthContext'
const SubscriptionUpgrade = () => {
	const authContext = useContext(AuthContext)
	const { products, isLoading } = useProducts()
	const [billingCycle, setBillingCycle] = useState('year')

	const origin = typeof window !== 'undefined' ? window.location.origin : ''
	const successUrl = origin + '/dashboard/conversations'
	const cancelUrl = origin + '/dashboard/conversations'

	const {
		companySubscription,
		subscriptionChangeLoading,
		currentSubscriptionPrice,
		currentSubscriptionProduct,
		handleSubscribe,
		confirmDialogOpen,
		pendingPlanChange,
		handleConfirmDialogConfirm,
		handleConfirmDialogCancel,
		enableSeatDialogOpen,
		dismissEnableSeatDialog,
		onSeatEnabled,
	} = useSubscribe({
		successUrl,
		cancelUrl,
	})

	const standardProduct: Product | undefined = useMemo(() => {
		return products?.find((product: Product) => product.planId === 'standard')
	}, [products])

	const yearlyPrice = useMemo(
		() => standardProduct?.prices.find(price => price.interval === 'year')?.amount ?? 0,
		[standardProduct?.prices],
	)
	const monthlyPrice = useMemo(
		() => standardProduct?.prices.find(price => price.interval === 'month')?.amount ?? 0,
		[standardProduct?.prices],
	)

	const yearlySavings = useMemo(() => (monthlyPrice * 12 - yearlyPrice).toFixed(2), [monthlyPrice, yearlyPrice])
	const currentPrice = useMemo(
		() => (billingCycle === 'year' ? yearlyPrice : monthlyPrice),
		[billingCycle, monthlyPrice, yearlyPrice],
	)

	const selectedPrice = useMemo(
		() => standardProduct?.prices.find(price => price.interval === billingCycle),
		[standardProduct, billingCycle],
	)

	const onUpgradeClick = () => {
		if (!selectedPrice || !standardProduct) return
		handleSubscribe({
			priceId: selectedPrice.id,
			productName: standardProduct.name,
			price: selectedPrice.amount,
			interval: selectedPrice.interval,
		})
	}

	return (
		<>
			<div className="flex items-center justify-center p-4">
				<Card className="max-w-md w-full">
					<CardHeader className="text-center">
						<div className="mx-auto mb-4 w-12 h-12 bg-primary rounded-full flex items-center justify-center">
							<Lock className="w-6 h-6 text-primary-foreground" />
						</div>
						<CardTitle className="text-2xl font-bold">Upgrade Required</CardTitle>
						<CardDescription>Access to this feature is available in our Standard plan</CardDescription>
					</CardHeader>
					<CardContent className="flex flex-col gap-2">
						{isLoading ? (
							<div className="flex justify-center p-6">
								<Loader />
							</div>
						) : (
							<>
								<FeatureList features={standardProduct?.marketingFeatures} />
								<RadioGroup
									defaultValue="year"
									onValueChange={setBillingCycle}
									className="flex justify-center space-x-4"
								>
									<div className="flex items-center space-x-2">
										<RadioGroupItem value="month" id="monthly" />
										<Label htmlFor="monthly">Monthly</Label>
									</div>
									<div className="flex items-center space-x-2">
										<RadioGroupItem value="year" id="yearly" />
										<Label htmlFor="yearly">Yearly</Label>
									</div>
								</RadioGroup>

								<div className="text-center space-y-2">
									<div>
										<span className="text-3xl font-bold">${currentPrice}</span>
										<span className="text-muted-foreground">
											/{billingCycle === 'year' ? 'year' : 'month'} per user
										</span>
									</div>
									{billingCycle === 'year' && (
										<div className="text-sm text-green-600 font-medium">
											Save ${yearlySavings} a year
										</div>
									)}
								</div>
							</>
						)}
					</CardContent>
					<CardFooter>
						<Button
							className="w-full"
							size="lg"
							onClick={onUpgradeClick}
							disabled={subscriptionChangeLoading || !selectedPrice}
						>
							{subscriptionChangeLoading ? 'Processing…' : 'Upgrade to Standard'}
						</Button>
					</CardFooter>
				</Card>
			</div>
			<EnableSeatDialog
				isOpen={enableSeatDialogOpen}
				onDismiss={dismissEnableSeatDialog}
				companyId={authContext.company?.id}
				onSeatEnabled={onSeatEnabled}
			/>
			<ConfirmPlanChangeDialog
				isOpen={confirmDialogOpen}
				currentPlan={
					currentSubscriptionProduct && currentSubscriptionPrice
						? {
								name: currentSubscriptionProduct.name,
								price: currentSubscriptionPrice.amount,
								interval: currentSubscriptionPrice.interval,
							}
						: { name: '', price: 0, interval: 'month' }
				}
				newPlan={
					pendingPlanChange
						? {
								name: pendingPlanChange.productName,
								price: pendingPlanChange.price,
								interval: pendingPlanChange.interval,
							}
						: null
				}
				onConfirm={handleConfirmDialogConfirm}
				onCancel={handleConfirmDialogCancel}
			/>
		</>
	)
}

export default SubscriptionUpgrade
